import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormGroup from "@mui/material/FormGroup";
import { SelectChangeEvent } from "@mui/material/Select/SelectInput";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { HolidayCountry } from "@sinch/core/entities/serverEnums";
import { useFormat } from "@sinch/core/hooks/format";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { pluck } from "ramda";
import React, { FC, useCallback, useMemo } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AvailableNationalHolidays } from "./queries.graphql";
import type { AvailableNationalHolidaysQuery, AvailableNationalHolidaysQueryVariables } from "./queries.types";
import { CheckboxInput } from "@sinch/components/form/CheckboxInput/CheckboxInput";
import { SelectInput } from "@sinch/components/form/SelectInput/SelectInput";
import { useMuiController } from "@sinch/components/index";

/**
 * Country picker plus per-holiday opt-out, shared by the national holidays interval and by the
 * "Attendance beginning on national holiday" rate condition. The weekend toggles only make sense
 * for the interval, which is why the condition hides them via `showWeekends`.
 */
export const NationalHolidaysSettings: FC<{
  disabled?: boolean;
  /** Prefix of the form field names, or an empty string when the fields sit at the form root */
  path?: string;
  showWeekends?: boolean;
  /**
   * Cap the holiday list at this height and scroll inside it, so the surrounding container grows by
   * a fixed amount no matter how many holidays the chosen country has. Also shows a selected counter.
   */
  listMaxHeight?: number;
}> = ({ disabled, path = "settings", showWeekends = true, listMaxHeight }) => {
  const { t, i18n } = useTranslation();
  const { date } = useFormat();
  const fieldName = (name: string) => (path === "" ? name : `${path}.${name}`);
  const { field: countryField } = useMuiController<FieldValues, string>({
    name: fieldName("country"),
    disabled,
  });
  const { field: excludedHolidaysField } = useMuiController<FieldValues, string>({
    name: fieldName("excludedHolidays"),
    disabled,
  });
  const { data, loading } = useQueryHandler<AvailableNationalHolidaysQuery, AvailableNationalHolidaysQueryVariables>(
    AvailableNationalHolidays,
    { country: countryField.value as AvailableNationalHolidaysQueryVariables["country"] },
    { skip: !countryField.value }
  );

  const excludedHolidays: string[] = excludedHolidaysField.value ?? [];

  const handleCountryChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newCountry = event.target.value;
      countryField.onChange(newCountry);
      excludedHolidaysField.onChange([]);
    },
    [countryField, excludedHolidaysField]
  );

  const handleHolidayToggle = useCallback(
    (holidayId: string) => {
      const isExcluded = excludedHolidays.includes(holidayId);
      const newExcluded = isExcluded
        ? excludedHolidays.filter((id) => id !== holidayId)
        : [...excludedHolidays, holidayId];
      excludedHolidaysField.onChange(newExcluded);
    },
    [excludedHolidays, excludedHolidaysField]
  );

  const handleGroupToggle = useCallback(
    (groupHolidayIds: string[]) => {
      const allIncluded = groupHolidayIds.every((id) => !excludedHolidays.includes(id));
      const newExcluded = allIncluded
        ? [...excludedHolidays, ...groupHolidayIds]
        : excludedHolidays.filter((id) => !groupHolidayIds.includes(id));
      excludedHolidaysField.onChange(newExcluded);
    },
    [excludedHolidays]
  );

  const holidays = data?.availableNationalHolidays ?? [];
  // excludedHolidays can still hold ids of a previously chosen country, so count against this list
  const selectedCount = holidays.filter(({ id }) => !excludedHolidays.includes(id)).length;

  const countryOptions = useMemo(() => {
    const countryNames = new Intl.DisplayNames([i18n.language], { type: "region" });

    return Object.values(HolidayCountry).map((country) => ({
      value: country as string,
      label: (countryNames.of(country) ?? country) as string,
    }));
  }, [i18n.language]);

  const groupedHolidays = useMemo(
    () =>
      Array.from(
        holidays
          .reduce((acc, el) => {
            const set = acc.get(el.group);
            acc.set(el.group, [...(set ?? []), el]);
            return acc;
          }, new Map<string | null, typeof holidays>())
          .entries()
      ).map(([key, val]) => ({
        key,
        list: val,
      })),
    [holidays]
  );

  return (
    <Stack spacing={2}>
      {loading ? (
        <Box sx={{ m: 1, textAlign: "center" }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <>
          <SelectInput
            {...countryField}
            disabled={disabled}
            disablePortal
            fullWidth
            label={t("CustomUnitType.nationalHolidays.country")}
            onChange={handleCountryChange}
            options={countryOptions}
          />

          <Stack alignItems="baseline" direction="row" justifyContent="space-between">
            <Typography variant="subtitle2">{t("CustomUnitType.nationalHolidays.publicHolidays")}</Typography>
            {listMaxHeight !== undefined && holidays.length > 0 && (
              <Typography color="grey.600" variant="descriptionTinySemi">
                {t("RateUnit.holidaysSelectedOfTotal", { selected: selectedCount, total: holidays.length })}
              </Typography>
            )}
          </Stack>
          <Box
            data-cy="NationalHolidaysSettings-holidayList"
            sx={listMaxHeight === undefined ? undefined : { maxHeight: listMaxHeight, overflowY: "auto", pr: 1 }}
          >
          <FormGroup>
            {groupedHolidays.map(({ key, list }) => {
              const groupHolidayIds = pluck("id", list);
              const allIncluded = groupHolidayIds.every((id) => !excludedHolidays.includes(id));
              const noneIncluded = groupHolidayIds.every((id) => excludedHolidays.includes(id));
              const indeterminate = !allIncluded && !noneIncluded;

              const isJustNull = key === null;

              return (
                <React.Fragment key={`${countryField.value}/${key}`}>
                  {!isJustNull && (
                    <CheckboxInput
                      checked={allIncluded}
                      disabled={disabled}
                      indeterminate={indeterminate}
                      label={key}
                      onChange={() => handleGroupToggle(groupHolidayIds)}
                    />
                  )}
                  <FormGroup sx={{ pl: isJustNull ? 0 : 3, pb: 1 }}>
                    {list.map((holiday) => (
                      <CheckboxInput
                        key={`${countryField.value}/${holiday.id}`}
                        checked={!excludedHolidays.includes(holiday.id)}
                        disabled={disabled}
                        label={
                          <Stack alignItems="baseline" direction="row" spacing={1}>
                            <span>{holiday.name}</span>
                            <Typography color="grey.600" variant="descriptionTinySemi">
                              ({date.medium(holiday.date)})
                            </Typography>
                          </Stack>
                        }
                        onChange={() => handleHolidayToggle(holiday.id)}
                      />
                    ))}
                  </FormGroup>
                </React.Fragment>
              );
            })}
          </FormGroup>
          </Box>
          {showWeekends && <WeekendToggles disabled={disabled} path={path} />}
        </>
      )}
    </Stack>
  );
};

/**
 * Saturday / Sunday opt-in of the national holidays interval. Kept in its own component so the
 * fields are only registered when the section is actually shown: a rate condition expresses
 * weekends through its weekday rows and must not persist these keys.
 */
const WeekendToggles: FC<{ disabled?: boolean; path: string }> = ({ disabled, path }) => {
  const { t } = useTranslation();
  const { field: saturdayField } = useMuiController<FieldValues, string>({
    name: `${path}.includeSaturday`,
    disabled,
  });
  const { field: sundayField } = useMuiController<FieldValues, string>({
    name: `${path}.includeSunday`,
    disabled,
  });

  return (
    <>
      <Divider />

      <Typography variant="subtitle2">{t("CustomUnitType.nationalHolidays.weekends")}</Typography>
      <FormGroup>
        <CheckboxInput {...saturdayField} checked={saturdayField.value} label={t("RateUnit.includeSaturdays")} />
        <CheckboxInput {...sundayField} checked={sundayField.value} label={t("RateUnit.includeSundays")} />
      </FormGroup>
    </>
  );
};
